import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { ingestBarcodeScan, withRouter } from "../../../shared/utility";
import { BarcodeScannerViewModel } from "../../../viewmodels/setup/barcodeScanner/BarcodeScannerViewModel";
import EmptyModal from "../../../components/modals/EmptyModal";
import AssemblyItemsListComponent from "../../../components/forms/setup/barcodeScanner/AssemblyItemsListComponent";
import * as actions from "../../../store/actions/index";

function BarcodeScanner(props) {
  const { addSnackbar, logout } = props;
  const {
    getAllAssemblyItems,
    assignAssemblyItem,
    setEmptyModelOpen,
    emptyModal,
    assemblyItems,
  } = BarcodeScannerViewModel(logout, addSnackbar);

  const [barcode, setBarcode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleScan = () => {
    if (!barcode.trim()) {
      setError("Please enter a barcode");
      setResult(null);
      return;
    }

    try {
      const parsed = ingestBarcodeScan(barcode);
      setResult(parsed);
      setError(null);
    } catch (err) {
      setError("Failed to parse barcode. Please check the format.");
      setResult(null);
    }
  };

  useEffect(() => {
    getAllAssemblyItems();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleScan();
    }
  };

  const handleClear = () => {
    setBarcode("");
    setResult(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleOpenAssemblyItems = async () => {
    const createListComponent = () => (
      <AssemblyItemsListComponent
        assemblyItemsList={assemblyItems}
        setOpen={setEmptyModelOpen}
        sku={result.plu}
        assignAssemblyItem={assignAssemblyItem}
        restoreListComponent={() => {
          setEmptyModelOpen({
            childComponent: createListComponent(),
            open: true,
          });
        }}
      />
    );

    setEmptyModelOpen({
      childComponent: createListComponent(),
      open: true,
    });
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Barcode Extract Tool
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                Enter a GS1 barcode to extract SKU, production date, weight, and
                serial number.
              </p>
            </div>
          </div>

          <div className="mt-8 card-custom p-6">
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="barcode"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Barcode
                </label>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    id="barcode"
                    name="barcode"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Scan your barcode here"
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleScan}
                    className="inline-flex justify-center rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Scan
                  </button>
                  {barcode && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="inline-flex justify-center rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {error}
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              {result && (
                <div className="rounded-md bg-green-50 p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Parsed Barcode Information
                  </h3>
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Supplier SKU
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">
                        {result.plu}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Production Date
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {result.date}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Net Weight
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {result.weight} kg
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Serial Number
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">
                        {result.serial}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleOpenAssemblyItems}
                    className="inline-flex justify-center rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    View Supplier Items
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BarcodeScanner),
);
